/**
 * ADR-0074 model-policy vocabularies — the closed value sets a policy is built
 * from, and the two matrices that say which combinations are legal.
 *
 * ── Why these live in core (ENG-9820) ───────────────────────────────────────
 *
 * They began in `packages/api/src/lib/model-policy-validate.ts`, which was the
 * only consumer. ENG-9820 adds a second: the org-settings authoring form needs
 * the same lists to populate its dropdowns, and the same matrices to avoid
 * offering a combination the database will refuse.
 *
 * The webapp cannot import from the API package, so leaving them there would
 * have created a THIRD definition of one rule — after the schema's CHECK
 * constraints and the validator's restatement of them. That is the ENG-8231
 * mirrored-allowlist shape, and this repo already carries two guards written
 * because of it. A shared module is the version with no drift to guard.
 *
 * What did NOT move: `validatePolicyInput` and its error messages. Those are a
 * message layer for one caller, they are not shared, and moving them would drag
 * the API's phrasing into a package the CLI also depends on.
 *
 * ── These are a MESSAGE layer, not an enforcement layer ─────────────────────
 *
 * The database is the authority. `model_policies` is unusually well defended —
 * two closed matrices, a biconditional on `base_url`, a wire-schema pin off
 * gateway, a no-secret-for-max-subscription rule, and CONSTRAINT TRIGGERS for
 * the one-primary invariant a row CHECK cannot express. Every one of those will
 * stop a bad write.
 *
 * What they will not do is explain it. A violation surfaces as `new row for
 * relation "model_policy_bindings" violates check constraint
 * "model_policy_bindings_source_transport_matrix"` — accurate, and useless to an
 * admin who picked "Bedrock" and "gateway token" from two dropdowns. So a bug in
 * this file can only produce a worse error message or a missing dropdown option,
 * never an invalid row.
 *
 * `packages/api/src/__tests__/eng-9486-model-policy-matrix-parity.test.ts`
 * parses the CHECK bodies out of the migration and asserts the matrices below
 * are the same relation, in both directions. It imports them from the API lib,
 * which re-exports this module — so the guard follows the values here.
 */

import { KNOWN_MODEL_SLOTS } from './agent.js';

export type ModelPolicyRole = 'primary' | 'secondary';
export type ModelPolicyTransport = 'anthropic-direct' | 'bedrock' | 'gateway';
export type ModelPolicyWireSchema = 'anthropic_messages' | 'openai_chat_completions';
export type ModelPolicyCredentialSource =
  | 'max_subscription'
  | 'api_key'
  | 'gateway_token'
  | 'aws_role';
export type ModelPolicyCredentialOwner = 'platform' | 'customer';
export type ModelPolicyCredentialMode = 'shared_secret' | 'per_agent_minted';

/**
 * ENG-9485: a slot name is framework-owned (FRAMEWORK_MODEL_SLOTS), so this is
 * deliberately not a closed union. See KNOWN_SLOTS below for what a WRITE is
 * checked against, and why that is the union rather than one framework's set.
 */
export type ModelPolicySlot = string;

export const MODEL_POLICY_ROLES: readonly ModelPolicyRole[] = ['primary', 'secondary'];

export const MODEL_POLICY_TRANSPORTS: readonly ModelPolicyTransport[] = [
  'anthropic-direct',
  'bedrock',
  'gateway',
];

export const MODEL_POLICY_WIRE_SCHEMAS: readonly ModelPolicyWireSchema[] = [
  'anthropic_messages',
  'openai_chat_completions',
];

export const MODEL_POLICY_CREDENTIAL_SOURCES: readonly ModelPolicyCredentialSource[] = [
  'max_subscription',
  'api_key',
  'gateway_token',
  'aws_role',
];

export const MODEL_POLICY_CREDENTIAL_OWNERS: readonly ModelPolicyCredentialOwner[] = [
  'platform',
  'customer',
];

export const MODEL_POLICY_CREDENTIAL_MODES: readonly ModelPolicyCredentialMode[] = [
  'shared_secret',
  'per_agent_minted',
];

/**
 * Known gateways, so a `gateway` binding can be chosen instead of typed.
 *
 * ── Why a preset and not a placeholder ──────────────────────────────────────
 *
 * A gateway binding needs a base URL that is exactly right and gives no useful
 * signal when it is wrong. Vercel's is the worked example: its Claude Code
 * endpoint takes NO `/v1` suffix, because the Anthropic SDK appends
 * `/v1/messages` itself — so the obvious URL, the one ending in the version
 * segment every other API in this file's neighbourhood wants, returns 404. That
 * is not a typo a validator can catch (it is a well-formed https URL for a real
 * host) and it is not a mistake anyone makes twice, which is precisely the
 * problem: it is made once, by whoever configures the fallback, and discovered
 * by whoever is depending on the fallback at the time it is needed.
 *
 * The preset is therefore not a convenience. It is the difference between a
 * fallback that has been typed correctly and one that has been chosen.
 *
 * ── What a preset does NOT set ──────────────────────────────────────────────
 *
 * `credential_source` stays the operator's. Both `gateway_token` and `api_key`
 * are legal on `gateway` (SOURCE_TRANSPORT_MATRIX above), the runtime does not
 * distinguish them on this transport — `resolveBindingCredential`'s gateway arm
 * never reads the source, and both resolve to `ANTHROPIC_AUTH_TOKEN` — and the
 * choice is about custody, which is a question about the organisation rather
 * than about the gateway.
 *
 * ── These are a MESSAGE layer too ───────────────────────────────────────────
 *
 * Same standing as the vocabularies above: the database validates the values, a
 * preset only supplies them. A wrong entry here produces a binding that saves
 * and does not route, never an invalid row — which is why `modelIdHint` is a
 * hint and the model warning below is a warning.
 */
export interface ModelPolicyGatewayPreset {
  /** Stable id. Never persisted — a binding stores base_url, not a preset. */
  readonly id: string;
  readonly label: string;
  readonly baseUrl: string;
  readonly wireSchema: ModelPolicyWireSchema;
  /**
   * The model-id shape this gateway routes on, shown where a model id is
   * entered. Vercel routes on `provider/model` and a bare Anthropic id is not
   * that, so the hint is the difference between a saved policy and a routed
   * request.
   */
  readonly modelIdHint: string;
  readonly docsUrl: string;
}

export const MODEL_POLICY_GATEWAY_PRESETS: readonly ModelPolicyGatewayPreset[] = [
  {
    id: 'vercel-ai-gateway',
    label: 'Vercel AI Gateway',
    // Vercel documents TWO base URLs that both work with Claude Code, and this
    // is the Claude Code-specific one (`vercel ai-gateway coding-agents setup`
    // writes it, and it is what enables the in-session `/model` picker). The
    // other is the bare host `https://ai-gateway.vercel.sh`, the general
    // Anthropic Messages surface. NEITHER takes a `/v1` you write yourself.
    baseUrl: 'https://ai-gateway.vercel.sh/claude-code',
    wireSchema: 'anthropic_messages',
    // Provider-prefixed, per Vercel's own `POST /v1/messages` example. The
    // `claude-code/` prefix seen in their model picker (e.g.
    // `claude-code/anthropic/claude-opus-5`) is a DISPLAY id — the gateway
    // strips it before routing — so it is not what belongs in a policy.
    modelIdHint: 'anthropic/claude-opus-5',
    docsUrl: 'https://vercel.com/docs/ai-gateway/coding-agents/claude-code',
  },
];

/**
 * Trailing slashes only. Deliberately not a general URL normalisation: two URLs
 * that differ in case or in an empty query string are different strings to
 * everything downstream, so treating them as equal HERE would show a preset as
 * selected while the binding held something else.
 */
function normalizeGatewayUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

/**
 * The preset a stored `base_url` corresponds to, or null for a custom gateway.
 *
 * Round-tripping is the point: without it a saved Vercel binding reopens as a
 * custom URL, and the next person to edit it inherits the free-text field this
 * exists to remove — including its `/v1` trap.
 */
export function gatewayPresetForBaseUrl(
  baseUrl: string | null | undefined,
): ModelPolicyGatewayPreset | null {
  if (!baseUrl) return null;
  const target = normalizeGatewayUrl(baseUrl);
  if (!target) return null;
  return (
    MODEL_POLICY_GATEWAY_PRESETS.find((p) => normalizeGatewayUrl(p.baseUrl) === target) ?? null
  );
}

/**
 * Why a model id looks wrong for a preset gateway, or null.
 *
 * A WARNING and not a rule, and the distinction is load-bearing. The model
 * families this form offers (`opus`, `sonnet`, …) are Claude Code's own aliases;
 * whether a gateway's compatibility surface resolves one is that gateway's
 * business, and Vercel's docs neither promise nor refuse it. What their docs DO
 * state is the format they document routing on, so that is what this says —
 * naming the observation, not a prediction about the request.
 *
 * Blocking the save on it would encode a guess as a constraint. Saying nothing
 * would let an operator pick "Opus" from a dropdown and get a fallback that
 * 404s at the moment it is promoted.
 *
 * The heuristic is weaker than a reader might assume, and the gap is worth
 * naming: `claude-code/anthropic/claude-opus-5` contains a slash and so passes
 * silently, but Vercel documents that prefix as a DISPLAY id their picker shows
 * and the gateway strips. Whether it routes is the same unobserved question as
 * a bare alias, and this says nothing about it. "Contains a provider prefix" is
 * what is actually checked; "is a model this gateway serves" is not knowable
 * from here.
 */
export function gatewayModelIdWarning(
  modelId: string,
  preset: ModelPolicyGatewayPreset | null,
): string | null {
  if (!preset) return null;
  const id = modelId.trim();
  if (!id || id.includes('/')) return null;
  return `${preset.label} routes on provider-prefixed model ids like ${preset.modelIdHint}. "${id}" is a Claude Code alias — check it resolves on this gateway before relying on it.`;
}

/**
 * ENG-9485 (ADR-0074 OQ9). Every slot name ANY framework can fill, derived from
 * FRAMEWORK_MODEL_SLOTS rather than restated — a second literal list is the
 * ENG-8231 mirrored-allowlist shape, where a name on one side and not the other
 * is dropped with no error.
 *
 * WHY THE UNION AND NOT ONE FRAMEWORK'S SET. A policy is assigned to an org, a
 * host or an agent — never to a framework — so one policy is routinely resolved
 * for agents of different frameworks. Validating a WRITE against, say,
 * opencode's single slot would make an org that runs both frameworks unable to
 * store the `small_fast` model its claude-code agents need. Which subset
 * actually applies is decided per agent at resolution time, by that agent's own
 * framework; a slot the agent's framework does not declare is not an error, it
 * is simply not consumed.
 *
 * This is therefore a typo-catcher, not an authorization rule: it rejects
 * `smallfast` and `opus`, and admits anything a live adapter declares.
 */
export const KNOWN_MODEL_POLICY_SLOTS: readonly string[] = [...KNOWN_MODEL_SLOTS].sort();

/**
 * The same slots in DECLARATION order — the order the frameworks list them in
 * `FRAMEWORK_MODEL_SLOTS`, which puts `primary` first because that is the one a
 * policy is mostly about.
 *
 * WHY THIS IS SEPARATE FROM THE SORTED SET ABOVE. `KNOWN_MODEL_POLICY_SLOTS` is
 * sorted so that a validation message reads the same every time, and that is the
 * right shape for a message. It is the wrong shape for a UI list, and the
 * difference was invisible while alphabetical order happened to agree with
 * declaration order: `['primary', 'small_fast']` sorts to itself.
 *
 * ENG-9811 broke the coincidence. `advisor` sorts FIRST, so the org-settings
 * authoring form — which offers `freeSlotsForRole(...)[0]` as the default for a
 * newly added model row — began defaulting new rows to the advisor slot instead
 * of the primary one. No error, no test of the sort itself: just a form that
 * quietly proposes the wrong thing, on a surface where the operator is likely to
 * accept the default.
 *
 * A `Set` preserves insertion order, so this is derived from the same source as
 * everything else here rather than being a third hand-maintained list.
 */
export const MODEL_POLICY_SLOTS_IN_ORDER: readonly string[] = [...KNOWN_MODEL_SLOTS];

/**
 * ADR-0074 §2a, `model_policy_bindings_source_transport_matrix`. CLOSED — a pair
 * absent here is invalid, not merely unconstrained. The migration's own comment
 * records why: an earlier cut wrote one-way implications, which left
 * `bedrock`+`api_key` and `anthropic-direct`+`gateway_token` admissible by
 * omission, failing at spawn as a vendor auth error on a policy someone had
 * already reviewed.
 *
 * `api_key` is valid on BOTH `anthropic-direct` and `gateway` deliberately —
 * that is the customer's own key routed through the customer's own gateway.
 */
export const SOURCE_TRANSPORT_MATRIX: Readonly<
  Record<ModelPolicyCredentialSource, readonly ModelPolicyTransport[]>
> = {
  max_subscription: ['anthropic-direct'],
  api_key: ['anthropic-direct', 'gateway'],
  gateway_token: ['gateway'],
  aws_role: ['bedrock'],
};

// ENG-10866: placed AFTER SOURCE_TRANSPORT_MATRIX, not before it. The first
// cut anchored on `export const SOURCE_TRANSPORT_MATRIX` and inserted there,
// which slid this block between that matrix's docblock and its declaration --
// orphaning the doc and attaching a comment about a CLOSED transport matrix to
// a constant about token counts, for every reader and every IDE hover
// (obi-wan, #6386). A value assertion cannot see that.
/**
 * The smallest context window Ninjafy will sell a gateway model on.
 *
 * A PRODUCT floor, decided 2026-09-22, not a technical one — and the two are
 * far apart, which is why this is written down rather than inferred. The
 * technical floor is around 18,000 tokens: `openai/gpt-3.5-turbo` (16,385)
 * cannot run Claude Code at all, failing on the FIRST turn with
 * `Prompt is too long` because the runtime's own baseline system prompt does
 * not fit (measured, CLI 2.1.278, 2026-09-22).
 *
 * 128,000 sits well above that and is the line the product draws. The
 * catalogue makes it a real filter rather than a formality: of 257 language
 * models on the gateway, 16 are below it — 8 under 50k — and several carry
 * names an operator would reasonably reach for in a fast slot.
 *
 * ── NOTHING ENFORCES THIS TODAY, AND THAT IS THE POINT ─────────────────────
 *
 * This docblock said "Read by `validatePolicyInput`" until obi-wan pointed out
 * that stopped being true inside the same PR. The floor WAS enforced on the
 * `context_window` value and was reverted, because `model_id` is unconstrained:
 * the rule never stopped a sub-floor model being selected, it stopped the
 * honest window being declared for one — so the agent ran at the runtime's
 * assumed 200,000 and failed mid-conversation. It blocked the fix and permitted
 * the hazard.
 *
 * The constant is therefore the RECORDED DECISION and nothing more, pinned by a
 * test so it cannot drift. Enforcement belongs with ENG-10867's producer, which
 * reads each model's real window from the gateway catalogue and can refuse the
 * MODEL rather than the number someone typed.
 *
 * Stated here because the next reader of this constant is most likely the
 * person building that producer, and a docblock claiming the API already
 * refuses a sub-floor window is exactly what would make them skip the check.
 * The measurement above is what stops the line being moved below ~18,000, where
 * the agent would not start at all.
 */
export const MIN_SUPPORTED_CONTEXT_WINDOW = 128_000;

/**
 * The slots whose model a context window may describe.
 *
 * There is ONE process-wide `CLAUDE_CODE_MAX_CONTEXT_TOKENS`, so only the slot
 * that actuates the runtime's main model can carry a window; a window stored
 * against any other slot is accepted, returned by the API and then silently
 * discarded at every spawn, which is the failure shape ENG-9811 already refuses
 * one rule below.
 *
 * Declared HERE rather than derived from the manager's `CLAUDE_CODE_SLOT_ENV`
 * because that map lives in `apps/cli` and the validator is in `packages/api`,
 * which cannot import it. The two are held together by an assertion in
 * `eng-10866-context-window.test.ts` rather than by hope — the ENG-8231
 * mirrored-allowlist shape, where a name present on one side and absent on the
 * other is dropped with no error.
 */
export const CONTEXT_WINDOW_BEARING_SLOTS: ReadonlySet<string> = new Set(['primary']);

/**
 * `model_policy_bindings_source_mode_matrix`. `per_agent_minted` is available to
 * exactly the two sources that can be minted per agent; the other two are
 * `shared_secret` only — `max_subscription` because one OAuth credential in
 * `/root/.claude` is shared by every agent on the host (ADR-0074 §3), and
 * `aws_role` because a role assumption is not a minted credential with a revoke
 * lifecycle.
 */
export const PER_AGENT_MINTED_SOURCES: readonly ModelPolicyCredentialSource[] = [
  'api_key',
  'gateway_token',
];

/**
 * The transports a given credential source may be paired with. Empty for a
 * source this build does not know, which a caller should treat as "offer
 * nothing" rather than "offer everything" — the closed matrix means an unknown
 * source has no legal pairing, not an unconstrained one.
 */
export function transportsForCredentialSource(
  source: string,
): readonly ModelPolicyTransport[] {
  return SOURCE_TRANSPORT_MATRIX[source as ModelPolicyCredentialSource] ?? [];
}

/** Does this credential source support `per_agent_minted`? */
export function supportsPerAgentMinted(source: string): boolean {
  return PER_AGENT_MINTED_SOURCES.includes(source as ModelPolicyCredentialSource);
}

/**
 * Does a gateway binding need a `base_url`? The schema enforces the
 * biconditional `transport = 'gateway' <-> base_url IS NOT NULL`, so this is the
 * one place a form needs to know it, in both directions: a gateway binding
 * without one is rejected, and a NON-gateway binding carrying one is rejected
 * too.
 */
export function requiresBaseUrl(transport: string): boolean {
  return transport === 'gateway';
}

/**
 * ENG-9811 — can a binding on this transport carry a SERVER-EXECUTED tool (the
 * `advisor` slot)?
 *
 * Deliberately TRI-STATE. A boolean would have to call the gateway cell one way
 * or the other, and both answers would be a guess presented as a fact: `false`
 * forbids a pairing that may well work, `true` permits one that fails silently.
 * `'unmeasured'` says which it is, and names the slice that answers it.
 *
 *   - `anthropic-direct` — MEASURED yes (CLI 2.1.261). Claude Code attaches the
 *     advisor only when its auth resolves to first-party, which this is.
 *   - `bedrock` — MEASURED no, and structurally so: Claude Code's own gate reads
 *     the Bedrock env var and stops before consulting `advisorModel` at all, so
 *     no policy value can make it attach. This is what the validator rejects.
 *   - `gateway` — UNMEASURED. Whether the advisor survives depends on whether
 *     that particular gateway forwards the request intact, which is a property
 *     of the operator's gateway rather than of the transport. ENG-9487's
 *     conformance probe is where this is answered; until then a gateway policy
 *     may store an advisor slot and it simply may not attach.
 *
 * See docs/research/eng-9811-advisor-model-delivery.md for the measurements.
 */
export const TRANSPORT_SERVER_TOOL_SUPPORT: Readonly<
  Record<ModelPolicyTransport, 'yes' | 'no' | 'unmeasured'>
> = {
  'anthropic-direct': 'yes',
  bedrock: 'no',
  gateway: 'unmeasured',
};

/**
 * Slots whose vocabulary exists but whose ACTUATOR does not yet — nothing,
 * anywhere, delivers them to a runtime.
 *
 * EMPTY SINCE ENG-10075, AND THAT IS THE POINT — it is a mechanism, not a list.
 *
 * ENG-9811 (CodeRabbit, PR #5604) put `advisor` here: the slot was declared,
 * validated and storable, but nothing wrote `advisorModel` anywhere, so a stored
 * row reached no host. ENG-10075 built that writer
 * (`writeProjectClaudeSettings`, apps/cli), so the entry was removed in the same
 * change — which is exactly the trigger the old comment named.
 *
 * WHY A FENCE RATHER THAN A NOTE, kept because the next slot will need it.
 * `FRAMEWORK_MODEL_SLOTS`'s docblock says an unconsumed slot "is not an error,
 * merely unconsumed", and that is true of the case it was written for: an
 * `opencode` agent ignoring `small_fast` is a policy row meant for somebody else.
 * A slot meant for THIS framework that nothing delivers is not that — an operator
 * who sets it gets no model, no error, and no way to tell it apart from one that
 * worked. Offering it in the authoring form would be inviting them into that.
 *
 * It fences the OFFER, never the WRITE: the API keeps accepting the row, so the
 * actuator slice can be tested against a real policy and an existing row still
 * renders. Only the dropdown withholds it.
 *
 * ADD A SLOT HERE the moment its vocabulary lands ahead of its actuator, and
 * delete it in the PR that builds the actuator — not before, and not in a
 * follow-up. `eng-9811-advisor-slot.test.ts` asserts the set's CONTENTS in both
 * directions, so neither half can drift out of step silently.
 */
export const MODEL_POLICY_SLOTS_PENDING_ACTUATOR: ReadonlySet<string> = new Set<string>();

/** Slots an authoring surface may offer: known, minus those nothing delivers yet. */
export const OFFERABLE_MODEL_POLICY_SLOTS: readonly string[] = MODEL_POLICY_SLOTS_IN_ORDER.filter(
  (slot) => !MODEL_POLICY_SLOTS_PENDING_ACTUATOR.has(slot),
);
